import { debounce } from 'lodash';
import React, { ReactElement, RefObject } from 'react';

export interface InfiniteScrollProps {
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  threshold?: number;
  loader?: ReactElement;
  children?: ReactElement;
  onInfinite: () => void | Promise<void>;
  scrollElement?:
    | HTMLElement
    | (() => Promise<HTMLElement | undefined>)
    | undefined;
  direction?: 'top' | 'bottom';
  disableScrollOnPositionChange?: boolean;
  debounceOnInfinite?: number;
}

interface ScrollPosition {
  scrollHeight: number;
  scrollTop: number;
  scrollOffset: number;
}

interface ScrollDetail {
  scrollTop: number;
  scrollLeft: number;
  startX: number;
  startY: number;
  startTime: number;
  currentX: number;
  currentY: number;
  velocityX: number;
  velocityY: number;
  deltaX: number;
  deltaY: number;
  currentTime: number;
  isScrolling: boolean;
  lastScroll: number;
}

class InfiniteScroll extends React.Component<InfiniteScrollProps> {
  private initialized = false;
  private loading = false;
  private observer: IntersectionObserver | undefined;
  private observerRef: RefObject<HTMLDivElement>;
  private previousPosition: ScrollPosition | undefined;
  private scroller: HTMLElement | undefined;
  private scrollDetail: ScrollDetail;
  private scrollInterval: NodeJS.Timeout | undefined;

  constructor(props: InfiniteScrollProps) {
    super(props);

    this.onIntersection = this.onIntersection.bind(this);
    this.initializeIntersectionObserver =
      this.initializeIntersectionObserver.bind(this);
    this.destroyIntersectionObserver =
      this.destroyIntersectionObserver.bind(this);

    this.getScrollPosition = this.getScrollPosition.bind(this);
    this.initializeScrollElement = this.initializeScrollElement.bind(this);
    this.updateScrollPosition = this.updateScrollPosition.bind(this);
    this.startScrollMonitor = this.startScrollMonitor.bind(this);
    this.stopScrollMonitor = this.stopScrollMonitor.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.observerRef = React.createRef<HTMLDivElement>();

    this.scrollDetail = {
      scrollTop: 0,
      scrollLeft: 0,
      startX: 0,
      startY: 0,
      startTime: 0,
      currentX: 0,
      currentY: 0,
      velocityX: 0,
      velocityY: 0,
      deltaX: 0,
      deltaY: 0,
      currentTime: 0,
      isScrolling: false,
      lastScroll: 0,
    };
  }

  public componentDidMount(): void {
    this.initializeIntersectionObserver();
  }

  public componentWillUnmount(): void {
    this.destroyIntersectionObserver();
    this.stopScrollMonitor();
  }

  public componentDidUpdate(): void {
    if (!this.initialized) {
      if (this.observerRef.current) {
        this.initializeIntersectionObserver();
      }
    } else {
      this.updateScrollPosition();
    }
  }

  public render(): ReactElement {
    const { disabled, children, className, direction, loader, style } =
      this.props;

    const mergedStyles = style ?? {};

    if (disabled) {
      mergedStyles.display = 'none';
    }

    const LoaderComponent = (
      <div
        ref={disabled ? undefined : this.observerRef}
        className={className}
        style={{ ...mergedStyles, minHeight: '30px' }}
      >
        {loader ?? <div>Loading...</div>}
      </div>
    );

    return (
      <>
        {direction === 'top' && LoaderComponent}
        {children}
        {direction !== 'top' && LoaderComponent}
      </>
    );
  }

  protected initializeIntersectionObserver(): void {
    if (this.initialized) {
      return;
    }

    const { threshold } = this.props;

    const options: IntersectionObserverInit = {
      threshold,
      rootMargin: '0px',
    };

    if (this.observer) {
      this.destroyIntersectionObserver();
    }

    this.observer = new IntersectionObserver(
      debounce(this.onIntersection, 100),
      options
    );

    const ref = this.observerRef.current;

    if (ref) {
      this.observer.observe(ref);
      this.initialized = true;
    }
  }

  protected destroyIntersectionObserver(): void {
    const ref = this.observerRef.current;

    if (ref && this.observer) {
      this.observer.unobserve(ref);
      this.observer.disconnect();
    }

    this.observer = undefined;
    this.initialized = false;
  }

  protected async onIntersection(
    entries: IntersectionObserverEntry[]
  ): Promise<void> {
    if (!this.scroller) {
      await this.initializeScrollElement();
    }

    const entry = entries[0];
    if (entry.isIntersecting) {
      if (this.loading) {
        return;
      }

      const { onInfinite, debounceOnInfinite } = this.props;

      this.previousPosition = this.getScrollPosition();

      this.loading = true;
      if (debounceOnInfinite) {
        await debounce(onInfinite, debounceOnInfinite)();
      } else {
        await onInfinite();
      }
      this.loading = false;
    }
  }

  protected updateScrollPosition(): void {
    if (this.scroller) {
      const current = this.getScrollPosition();

      if (this.previousPosition && current) {
        const disableScroll = this.props.disableScrollOnPositionChange;

        if (disableScroll) {
          this.scroller.style.overflow = 'hidden';
        }

        this.scroller.scrollBy(
          0,
          Math.max(
            this.scroller.scrollHeight - this.previousPosition.scrollHeight,
            (this.observerRef.current?.clientHeight ?? 0) + 1
          )
        );

        if (disableScroll) {
          this.scroller.style.overflow = 'scroll';
        }
      }
    }
  }

  protected getScrollPosition(): ScrollPosition | undefined {
    if (this.scroller) {
      return {
        scrollHeight: this.scroller.scrollHeight,
        scrollTop: this.scroller.scrollTop,
        scrollOffset: this.scroller.offsetHeight,
      };
    }
  }

  protected async initializeScrollElement(): Promise<HTMLElement | undefined> {
    if (this.scroller === undefined) {
      const { scrollElement } = this.props;

      if (typeof scrollElement === 'function') {
        this.scroller = await scrollElement();
      } else {
        this.scroller = scrollElement;
      }

      if (this.scroller) {
        this.startScrollMonitor();
      }
    }

    return this.scroller;
  }

  protected startScrollMonitor(): void {
    if (this.scroller) {
      this.scroller.addEventListener('scroll', this.onScroll);
    }
  }

  protected stopScrollMonitor(): void {
    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.onScroll);
    }
  }

  protected onScroll(): void {
    const timestamp = Date.now();
    const shouldStart = !this.scrollDetail.isScrolling;

    this.scrollDetail.lastScroll = timestamp;

    if (shouldStart) {
      this.onScrollStart();
    }

    if (this.scroller) {
      this.updateScrollDetail(
        this.scrollDetail,
        this.scroller,
        timestamp,
        shouldStart
      );
    }
  }

  protected onScrollStart(): void {
    this.scrollDetail.isScrolling = true;

    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
    }

    this.scrollInterval = setInterval(() => {
      if (this.scrollDetail.lastScroll < Date.now() - 120) {
        this.onScrollEnd();
      }
    }, 100);
  }

  protected onScrollEnd(): void {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
    }

    this.scrollInterval = undefined;
    this.scrollDetail.isScrolling = false;
  }

  protected updateScrollDetail(
    detail: ScrollDetail,
    el: Element,
    timestamp: number,
    shouldStart: boolean
  ): void {
    const prevX = detail.currentX;
    const prevY = detail.currentY;
    const prevT = detail.currentTime;
    const currentX = el.scrollLeft;
    const currentY = el.scrollTop;
    const timeDelta = timestamp - prevT;

    if (shouldStart) {
      detail.startTime = timestamp;
      detail.startX = currentX;
      detail.startY = currentY;
      detail.velocityX = detail.velocityY = 0;
    }
    detail.currentTime = timestamp;
    detail.currentX = detail.scrollLeft = currentX;
    detail.currentY = detail.scrollTop = currentY;
    detail.deltaX = currentX - detail.startX;
    detail.deltaY = currentY - detail.startY;

    if (timeDelta > 0 && timeDelta < 100) {
      const velocityX = (currentX - prevX) / timeDelta;
      const velocityY = (currentY - prevY) / timeDelta;
      detail.velocityX = velocityX * 0.7 + detail.velocityX * 0.3;
      detail.velocityY = velocityY * 0.7 + detail.velocityY * 0.3;
    }
  }
}

export { InfiniteScroll };
